import { type AuthUser } from '@gb-ticket/shared-types';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { cn } from '@/lib/utils';
import { NavAppTile } from '@/platform/components/nav-app-tile';
import {
  menuModuleForPath,
  menuModules,
  tilesForMenuModule,
  type MenuModuleId,
} from '@/platform/lib/overview-catalog';
import { productNavRegistration, type ProductMenuTile } from '@/platform/lib/product-nav';
import { useOverviewLayout } from '@/platform/overview/use-overview-layout';

function OverviewMenuTiles({
  tiles,
  pending,
  onOpen,
  onLongPress,
}: {
  tiles: readonly ProductMenuTile[];
  pending: boolean;
  onOpen: (tile: ProductMenuTile) => void;
  onLongPress: (tile: ProductMenuTile) => void;
}) {
  if (pending) {
    return <p className="px-1 text-sm text-muted-foreground">Übersicht wird geladen …</p>;
  }
  if (tiles.length === 0) {
    return (
      <p className="px-1 text-sm text-muted-foreground">
        Halten Sie im Menü eine Kachel gedrückt, um sie hier abzulegen.
      </p>
    );
  }
  return <ModuleTileSections tiles={tiles} onOpen={onOpen} onLongPress={onLongPress} />;
}

function ModuleTileSections({
  tiles,
  soon,
  onOpen,
  onLongPress,
}: {
  tiles: readonly ProductMenuTile[];
  soon?: boolean;
  onOpen: (tile: ProductMenuTile) => void;
  onLongPress: (tile: ProductMenuTile) => void;
}) {
  const location = useLocation();
  if (soon) {
    return (
      <ul className="nav-menu-grid">
        <li className="nav-tile-in min-w-0">
          <NavAppTile label="Demnächst" path="/tools" disabled />
        </li>
      </ul>
    );
  }
  return (
    <>
      {groupTiles(tiles).map((group) => (
        <section key={group.label ?? 'default'} className="nav-menu-group">
          {group.label ? (
            <h2 className="text-[0.72rem] font-bold tracking-[0.06em] text-foreground uppercase">
              {group.label}
            </h2>
          ) : null}
          <ul className="nav-menu-grid">
            {group.tiles.map((tile, index) => (
              <li
                key={tile.id}
                className="nav-tile-in min-w-0"
                style={{ animationDelay: `${Math.min(index, 12) * 26}ms` }}
              >
                <NavAppTile
                  label={tile.label}
                  path={tile.to}
                  icon={tile.icon}
                  active={tileIsCurrent(location.pathname, tile.to)}
                  onOpen={() => onOpen(tile)}
                  onLongPress={() => onLongPress(tile)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

function tileIsCurrent(pathname: string, to: string): boolean {
  if (pathname === to) {
    return true;
  }
  const depth = to.split('/').filter(Boolean).length;
  return depth >= 2 && pathname.startsWith(`${to}/`);
}

function ProductModuleTiles({
  moduleId,
  user,
  soon,
  onOpen,
  onLongPress,
}: {
  moduleId: MenuModuleId;
  user: AuthUser;
  soon?: boolean;
  onOpen: (tile: ProductMenuTile) => void;
  onLongPress: (tile: ProductMenuTile) => void;
}) {
  const hook = productNavRegistration(moduleId as never)?.useExtraTiles;
  if (!hook) {
    return (
      <ModuleTileSections
        tiles={tilesForMenuModule(moduleId, user)}
        soon={soon}
        onOpen={onOpen}
        onLongPress={onLongPress}
      />
    );
  }
  return (
    <ProductModuleTilesWithExtras
      hook={hook}
      moduleId={moduleId}
      user={user}
      soon={soon}
      onOpen={onOpen}
      onLongPress={onLongPress}
    />
  );
}

function ProductModuleTilesWithExtras({
  hook,
  moduleId,
  user,
  soon,
  onOpen,
  onLongPress,
}: {
  hook: () => ProductMenuTile[];
  moduleId: MenuModuleId;
  user: AuthUser;
  soon?: boolean;
  onOpen: (tile: ProductMenuTile) => void;
  onLongPress: (tile: ProductMenuTile) => void;
}) {
  const extras = hook();
  return (
    <ModuleTileSections
      tiles={tilesForMenuModule(moduleId, user, extras)}
      soon={soon}
      onOpen={onOpen}
      onLongPress={onLongPress}
    />
  );
}

export function PlatformMenuOverlay({
  open,
  onOpenChange,
  user,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AuthUser;
}) {
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    if (open) {
      root.dataset.mobileMenu = 'open';
    } else {
      delete root.dataset.mobileMenu;
    }
    return () => {
      delete root.dataset.mobileMenu;
    };
  }, [open]);

  return (
    <div
      className={cn(
        'fixed inset-x-0 top-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-[70] lg:hidden',
        open && 'mobile-menu-material',
        !open && 'pointer-events-none',
      )}
    >
      {open ? (
        <MenuPanel
          key={location.pathname}
          user={user}
          pathname={location.pathname}
          onOpenChange={onOpenChange}
        />
      ) : null}
    </div>
  );
}

function MenuPanel({
  user,
  pathname,
  onOpenChange,
}: {
  user: AuthUser;
  pathname: string;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const overview = useOverviewLayout(user);
  const modules = menuModules(user);
  const [selected, setSelected] = useState<MenuModuleId>(() => menuModuleForPath(pathname));
  const [action, setAction] = useState<ProductMenuTile | null>(null);
  const selectedModule = modules.find((module) => module.id === selected);
  const useExtras = Boolean(productNavRegistration(selected as never)?.useExtraTiles);
  const drag = useRef<{ x: number; y: number; id: number; horizontal: boolean } | null>(null);
  const [offset, setOffset] = useState(0);
  const [sliding, setSliding] = useState(false);

  useEffect(() => {
    document
      .querySelector(`[data-menu-module="${selected}"]`)
      ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [selected]);

  function stepModule(direction: -1 | 1) {
    const index = modules.findIndex((module) => module.id === selected);
    const next = modules[index + direction];
    if (!next) {
      return;
    }
    setSelected(next.id);
    setAction(null);
  }

  function onPanePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    drag.current = { x: event.clientX, y: event.clientY, id: event.pointerId, horizontal: false };
    setSliding(false);
  }

  function onPanePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const start = drag.current;
    if (start?.id !== event.pointerId) {
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (!start.horizontal && Math.abs(dx) < 12 && Math.abs(dy) < 12) {
      return;
    }
    if (!start.horizontal && Math.abs(dy) > Math.abs(dx)) {
      drag.current = null;
      return;
    }
    if (!start.horizontal) {
      start.horizontal = true;
      setSliding(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    const index = modules.findIndex((module) => module.id === selected);
    const blocked = (index <= 0 && dx > 0) || (index >= modules.length - 1 && dx < 0);
    setOffset(blocked ? dx * 0.35 : dx);
  }

  function onPanePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    const start = drag.current;
    drag.current = null;
    if (start?.id !== event.pointerId) {
      setSliding(false);
      setOffset(0);
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) >= 56 && Math.abs(dx) > Math.abs(dy)) {
      stepModule(dx < 0 ? 1 : -1);
    }
    setSliding(false);
    setOffset(0);
  }

  function openTile(tile: ProductMenuTile) {
    swallowClickAfterTouch();
    onOpenChange(false);
    if (tile.to.startsWith('http://') || tile.to.startsWith('https://')) {
      window.location.assign(tile.to);
      return;
    }
    void navigate(tile.to);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Menü"
      className="nav-menu-in nav-menu-sheet relative flex h-full min-h-0 flex-col"
    >
      <div className="nav-menu-head">
        <h2 className="text-[1.45rem] font-bold tracking-tight text-foreground">Menü</h2>
      </div>
      <div className="[scrollbar-width:none] overflow-x-auto">
        <ul className="nav-menu-jumps" aria-label="Module">
          {modules.map((module) => {
            const active = module.id === selected;
            return (
              <li key={module.id}>
                <button
                  type="button"
                  data-menu-module={module.id}
                  onClick={() => {
                    setSelected(module.id);
                    setAction(null);
                  }}
                  className={cn(
                    'nav-menu-jump rounded-full whitespace-nowrap',
                    active ? 'bg-primary text-primary-foreground' : 'bg-card/75 text-foreground',
                  )}
                >
                  {module.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div
        className="nav-menu-scroll touch-pan-y overflow-x-hidden"
        onPointerDown={onPanePointerDown}
        onPointerMove={onPanePointerMove}
        onPointerUp={onPanePointerUp}
        onPointerCancel={onPanePointerUp}
      >
        <div
          className={cn(!sliding && 'transition-transform duration-200 ease-out')}
          style={offset ? { transform: `translateX(${offset}px)` } : undefined}
        >
          {selected === 'overview' ? (
            <OverviewMenuTiles
              tiles={overview.tiles.map((tile) => ({
                id: tile.id,
                label: tile.label,
                to: tile.path,
                icon: tile.icon,
              }))}
              pending={overview.isPending}
              onOpen={openTile}
              onLongPress={setAction}
            />
          ) : useExtras ? (
            <ProductModuleTiles
              moduleId={selected}
              user={user}
              soon={selectedModule?.soon}
              onOpen={openTile}
              onLongPress={setAction}
            />
          ) : (
            <ModuleTileSections
              tiles={tilesForMenuModule(selected, user)}
              soon={selectedModule?.soon}
              onOpen={openTile}
              onLongPress={setAction}
            />
          )}
        </div>
      </div>
      {action ? (
        <div className="absolute inset-x-4 bottom-6 rounded-2xl border border-border bg-card p-3 shadow-lg">
          <p className="mb-2 text-sm font-medium">{action.label}</p>
          <button
            type="button"
            className="w-full rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
            onClick={() => {
              overview.togglePath(action.to);
              setAction(null);
            }}
          >
            {overview.hasPath(action.to) ? 'Entfernen' : 'Zur Übersicht'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The touch ends on the tile, then the click lands on whatever is underneath once the menu closes. */
function swallowClickAfterTouch() {
  const stop = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  document.addEventListener('click', stop, true);
  window.setTimeout(() => document.removeEventListener('click', stop, true), 700);
}

function groupTiles(
  tiles: readonly ProductMenuTile[],
): { label?: string; tiles: ProductMenuTile[] }[] {
  const groups: { label?: string; tiles: ProductMenuTile[] }[] = [];
  for (const tile of tiles) {
    const last = groups.at(-1);
    if (last && last.label === tile.group) {
      last.tiles.push(tile);
    } else {
      groups.push({ label: tile.group, tiles: [tile] });
    }
  }
  return groups;
}
